/**
 * Parametric EQ filter list and its real (combined) frequency response.
 *
 * The parametric export writes one Equalizer APO `PK` filter per preset band. On a dense
 * grid with Q ≈ 3 neighbouring boosts overlap and add up, so the response peak can sit
 * several dB above the highest band gain. The preamp therefore has to come from the
 * simulated response, not from the band gains.
 */

import type { EQSetting } from '../types/index';
import { interpAt, computeQForFrequencies } from './math-utils';

export interface PeakingFilter {
    frequency: number;
    gain: number;
    q: number;
}

export interface BuildParametricFiltersOptions {
    presetFrequencies: number[];
    peakQuality?: number;
}

/** Sample rates the response is checked at; the preamp must be safe at either. */
const SAMPLE_RATES = [44100, 48000];
/** Highest checked frequency as a fraction of Nyquist (where every PK filter is back at 0 dB). */
const TOP_FRACTION = 0.999;
const LOWEST_FREQUENCY = 20;
const GRID_POINTS = 2000;

/**
 * Magnitude in dB of one peaking filter at `freq`, using Equalizer APO's `PEAKING` biquad
 * (filters/BiQuad.cpp: the RBJ cookbook peaking EQ, alpha = sin(w0) / (2Q)) and the
 * closed-form magnitude from its BiQuad::gainAt.
 */
function peakingGainDb(filter: PeakingFilter, freq: number, sampleRate: number): number {
    const A = Math.pow(10, filter.gain / 40);
    const w0 = 2 * Math.PI * filter.frequency / sampleRate;
    const alpha = Math.sin(w0) / (2 * filter.q);
    const cs = Math.cos(w0);

    const a0 = 1 + alpha / A;
    const b0 = (1 + alpha * A) / a0;
    const b1 = (-2 * cs) / a0;
    const b2 = (1 - alpha * A) / a0;
    const a1 = (-2 * cs) / a0;
    const a2 = (1 - alpha / A) / a0;

    const sn = Math.sin(Math.PI * freq / sampleRate);
    const phi = sn * sn;
    const magSq = (c0: number, c1: number, c2: number) =>
        Math.pow(c0 + c1 + c2, 2) - 4 * (c0 * c1 + 4 * c0 * c2 + c1 * c2) * phi + 16 * c0 * c2 * phi * phi;
    return 10 * Math.log10(magSq(b0, b1, b2)) - 10 * Math.log10(magSq(1, a1, a2));
}

function responseDb(filters: PeakingFilter[], freq: number, sampleRate: number): number {
    let db = 0;
    for (const filter of filters) db += peakingGainDb(filter, freq, sampleRate);
    return db;
}

/**
 * Peak of the filters' combined magnitude response in dB, over 20 Hz to just below
 * Nyquist at 44.1 and 48 kHz. Scans a log-spaced grid (plus every filter centre), then
 * refines each local maximum with a golden-section search so narrow peaks aren't missed.
 */
function combinedPeakDb(filters: PeakingFilter[]): number {
    if (!filters.length) return 0;
    let peak = -Infinity;

    for (const fs of SAMPLE_RATES) {
        const top = TOP_FRACTION * fs / 2;
        const freqs: number[] = [];
        for (let i = 0; i <= GRID_POINTS; i++) freqs.push(LOWEST_FREQUENCY * Math.pow(top / LOWEST_FREQUENCY, i / GRID_POINTS));
        filters.forEach(f => { if (f.frequency > LOWEST_FREQUENCY && f.frequency < top) freqs.push(f.frequency); });
        freqs.sort((a, b) => a - b);

        const levels = freqs.map(f => responseDb(filters, f, fs));
        for (let i = 0; i < freqs.length; i++) {
            peak = Math.max(peak, levels[i]);
            const isLocalMax = (i === 0 || levels[i] >= levels[i - 1]) && (i === freqs.length - 1 || levels[i] >= levels[i + 1]);
            if (!isLocalMax || i === 0 || i === freqs.length - 1) continue;

            // Golden-section search on log-frequency between the neighbouring grid points.
            let lo = Math.log(freqs[i - 1]);
            let hi = Math.log(freqs[i + 1]);
            const ratio = (Math.sqrt(5) - 1) / 2;
            for (let k = 0; k < 40; k++) {
                const m1 = hi - ratio * (hi - lo);
                const m2 = lo + ratio * (hi - lo);
                if (responseDb(filters, Math.exp(m1), fs) < responseDb(filters, Math.exp(m2), fs)) lo = m1;
                else hi = m2;
            }
            peak = Math.max(peak, responseDb(filters, Math.exp((lo + hi) / 2), fs));
        }
    }

    return peak;
}

/**
 * Preamp that puts the filters' real response peak at -headroom dB:
 * -(peak + headroom), rounded down to 0.1 dB so it can never land above -headroom.
 */
function calculateParametricPreamp(filters: PeakingFilter[], headroom = 0.2): number {
    if (!filters.length) return 0;
    const preamp = -(combinedPeakDb(filters) + headroom);
    // The 1e-9 keeps an exact -6.2 from flooring to -6.3 through float error;
    // "+ 0" turns -0 into 0 so an exact fit doesn't print as "-0.0".
    return Math.floor(preamp * 10 + 1e-9) / 10 + 0;
}

/**
 * The exact PK filters the parametric export writes: one per preset band, gains rounded
 * to 0.01 dB and Qs to 0.01 as printed.
 *
 * Peace computes Q over the full slider grid (tested or not), so every band's Q comes from
 * the preset grid with the configured peak-quality index; a tested band's own Q wins.
 * Untested bands get their gain filled the way Peace does: flat-extended at the edges,
 * linear-in-frequency between tested neighbors (Peace16911.au3:6397-6406).
 */
function buildParametricFilters(eqSettings: EQSetting[], options: BuildParametricFiltersOptions): PeakingFilter[] {
    const { presetFrequencies } = options;
    const sortedSettings = (eqSettings || []).slice().sort((a: EQSetting, b: EQSetting) => a.frequency - b.frequency);

    const sortedGrid = presetFrequencies.slice().sort((a: number, b: number) => a - b);
    const gridQs = computeQForFrequencies(sortedGrid, options.peakQuality ?? 0);
    const gridQAt = (freq: number): number => {
        const gi = sortedGrid.findIndex(g => Math.abs(g - freq) < 1);
        return gi >= 0 ? gridQs[gi] : 1.41;
    };

    return presetFrequencies.map((freq: number) => {
        let gain = 0;
        let q = gridQAt(freq);

        if (sortedSettings.length > 0) {
            const exact = sortedSettings.find(s => Math.abs(s.frequency - freq) < 1);
            if (exact) {
                gain = exact.gain || 0;
                if (typeof exact.q === 'number' && isFinite(exact.q)) q = exact.q;
            } else {
                let lower: EQSetting | null = null;
                let upper: EQSetting | null = null;
                for (let i = 0; i < sortedSettings.length; i++) {
                    if (sortedSettings[i].frequency <= freq) lower = sortedSettings[i];
                    if (sortedSettings[i].frequency >= freq && !upper) upper = sortedSettings[i];
                }
                if (lower && upper) {
                    gain = (lower.frequency === upper.frequency)
                        ? (lower.gain || 0)
                        : interpAt(freq, [lower.frequency, lower.gain] as any, [upper.frequency, upper.gain] as any, 'linear', 'linear');
                } else if (lower) {
                    gain = lower.gain || 0;
                } else if (upper) {
                    gain = upper.gain || 0;
                }
            }
        }

        if (!isFinite(q) || q <= 0) q = 1.41;

        return { frequency: freq, gain: Number(gain.toFixed(2)), q: Number(q.toFixed(2)) };
    });
}

export { peakingGainDb, combinedPeakDb, calculateParametricPreamp, buildParametricFilters };
